/**
 * <NpsModal> — NPS survey modal shell (template, L0.1).
 *
 * Wraps `<NpsSurvey>` in the DS `<Modal>`. Routes the header close button to
 * `onDismissWithoutScore` vs `onSkipComment` based on whether a score was accepted.
 *
 * The thanks-screen "Fermer" is a THIRD path: the comment was submitted, so it must close silently —
 * routing it through `onSkipComment` would report every commenter as a skipper (legacy closes the
 * modal directly on comment success and never reaches its skip branch).
 */
import { useEffect, useState } from "react"

import { Modal } from "../../molecules/Modal"
import { NpsSurvey } from "../../organisms/NpsSurvey"

export type NpsModalProps = {
    visible: boolean
    onClose: () => void
    onSelectScore: (score: number) => Promise<void> | void
    onSubmitComment: (score: number, comment: string) => Promise<void> | void
    /** Close before score accepted (frequency dismiss). */
    onDismissWithoutScore: () => void
    /** Close after a score was accepted but no comment was sent. */
    onSkipComment: (score: number) => void
    commentSubmitting?: boolean
    testID?: string
}

export function NpsModal(props: NpsModalProps) {
    const {
        visible,
        onClose,
        onSelectScore,
        onSubmitComment,
        onDismissWithoutScore,
        onSkipComment,
        commentSubmitting,
        testID,
    } = props

    const [acceptedScore, setAcceptedScore] = useState<number | null>(null)
    const [commentSubmitted, setCommentSubmitted] = useState(false)

    useEffect(() => {
        if (!visible) {
            setAcceptedScore(null)
            setCommentSubmitted(false)
        }
    }, [visible])

    // Tracked on the flag, not on which control was used, so dismissing the thanks screen by backdrop /
    // Escape / Android back is silent too — not just the "Fermer" button.
    const handleClose = () => {
        if (commentSubmitted) {
            // Already reported by onSubmitComment — closing adds nothing.
        } else if (acceptedScore != null) {
            onSkipComment(acceptedScore)
        } else {
            onDismissWithoutScore()
        }
        onClose()
    }

    return (
        <Modal visible={visible} onClose={handleClose} title="Votre avis" dismissable fitContent testID={testID}>
            <NpsSurvey
                resetKey={visible}
                onSelectScore={onSelectScore}
                onSubmitComment={onSubmitComment}
                onScoreAccepted={setAcceptedScore}
                onCommentSubmitted={() => setCommentSubmitted(true)}
                onFinished={handleClose}
                commentSubmitting={commentSubmitting}
            />
        </Modal>
    )
}
